"use client";

import { c as _c } from "react/compiler-runtime";
import { publicEnv } from "@/config/env";
import { useCallback, useMemo, useState } from "react";
import { TraitsData } from "../types/TraitsData";
import { validateTraitsData } from "./traitsValidation";
import { ValidationOptions, ValidationResult } from "./validationTypes";

/**
 * Custom hook for form validation
 *
 * @param traits Current form data
 * @param initialTraits Initial form data (for detecting dirty fields)
 * @returns Validation state and handlers
 */
export function useTraitsValidation(traits, initialTraits) {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [touchedFields, setTouchedFields] = useState(t0);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const validationMode = submitAttempted ? "all" : "touched";
  let t1;
  if ($[1] !== initialTraits || $[2] !== touchedFields || $[3] !== validationMode) {
    t1 = {
      mode: validationMode,
      touchedFields,
      initialValues: initialTraits
    };
    $[1] = initialTraits;
    $[2] = touchedFields;
    $[3] = validationMode;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const validationOptions = t1;
  let t2;
  if ($[5] !== traits || $[6] !== validationOptions) {
    t2 = validateTraitsData(traits, validationOptions);
    $[5] = traits;
    $[6] = validationOptions;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const validationResult = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = field => {
      setTouchedFields(prev => {
        const newSet = new Set(prev);
        newSet.add(field);
        return newSet;
      });
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const markFieldTouched = t3;
  let t4;
  if ($[9] !== traits) {
    t4 = () => {
      setTouchedFields(new Set(Object.keys(traits) as Array<keyof TraitsData>));
    };
    $[9] = traits;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const markAllFieldsTouched = t4;
  let t5;
  if ($[11] !== traits) {
    t5 = () => {
      setSubmitAttempted(true);
      return validateTraitsData(traits, {
        mode: "all"
      });
    };
    $[11] = traits;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const validateAll = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setTouchedFields(new Set());
      setSubmitAttempted(false);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const resetValidation = t6;
  let t7;
  if ($[14] !== validationResult.firstInvalidField) {
    t7 = () => {
      if (!validationResult.firstInvalidField) {
        return;
      }
      const field_0 = String(validationResult.firstInvalidField);
      const selectors = [`[name="${field_0}"]`, `#field-${field_0.toLowerCase().replace(/\s+/g, "-")}`, `[data-field="${field_0}"]`, `[aria-label*="${field_0}"]`, `input[id*="${field_0}"], textarea[id*="${field_0}"], select[id*="${field_0}"]`, `label[for*="${field_0}"]`, `label:contains("${field_0}")`];
      let fieldElement = null;
      for (const selector of selectors) {
        ;
        try {
          const element = document.querySelector(selector);
          if (element instanceof HTMLElement) {
            fieldElement = element;
            break;
          }
        } catch (t8) {
          const error = t8;
          continue;
        }
      }
      if (fieldElement) {
        ;
        try {
          fieldElement.focus();
          ;
          try {
            fieldElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest"
            });
          } catch (t10) {
            const scrollError = t10;
            fieldElement.scrollIntoView(false);
          }
          fieldElement.classList.add("tw-highlight-focus");
          setTimeout(() => {
            fieldElement?.classList.remove("tw-highlight-focus");
          }, 2000);
        } catch (t9) {
          const rect = fieldElement.getBoundingClientRect();
          window.scrollTo({
            top: rect.top + window.scrollY - 100,
            behavior: "smooth"
          });
        }
      } else {
        if (publicEnv.NODE_ENV === "development") {
          console.warn(`Could not find element to focus for field: ${field_0}`);
        }
      }
    };
    $[14] = validationResult.firstInvalidField;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const focusFirstInvalidField = t7;
  let t8;
  if ($[16] !== focusFirstInvalidField || $[17] !== markAllFieldsTouched || $[18] !== submitAttempted || $[19] !== touchedFields || $[20] !== validateAll || $[21] !== validationResult) {
    t8 = {
      isValid: validationResult.isValid,
      errors: validationResult.errors,
      errorCount: validationResult.errorCount,
      touchedFields,
      submitAttempted,
      markFieldTouched,
      markAllFieldsTouched,
      validateAll,
      resetValidation,
      focusFirstInvalidField,
      validationResult
    };
    $[16] = focusFirstInvalidField;
    $[17] = markAllFieldsTouched;
    $[18] = submitAttempted;
    $[19] = touchedFields;
    $[20] = validateAll;
    $[21] = validationResult;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}